/**
 * OpenAI Responses API adapter for the Excel multi-hop executor.
 *
 * Responses accepts a flat `input` list of items in any order, so the canonical
 * IR maps across almost one-to-one: model turns are spread back in verbatim and
 * user and tool-result turns become single items. The `json_object` text format
 * set here is what the executor's terminal JSON contract relies on.
 */

import { buildApiUrl } from "../../shared/config";
import { requestOpenAIResponsesStreamWithRetry } from "../excelAIService";
import { buildToolDefinitionsForProvider } from "../toolRegistry";
import {
  MAX_REPLAYED_TOOL_OUTPUT_CHARS,
  truncateReplayedToolOutput,
} from "./replayTruncation";
import type {
  AgentToolCall,
  AgentTurn,
  AgentTurnResult,
  ExecutorBuildRequestParams,
  ExecutorProviderAdapter,
  ExecutorProviderRequest,
  ExecutorSerializeToolsParams,
  ExecutorStreamTurnParams,
} from "./types";

// ---------------------------------------------------------------------------
// Responses API types (minimal)
// ---------------------------------------------------------------------------

interface OpenAiFunctionCall {
  type: "function_call";
  call_id: string;
  name: string;
  arguments: string;
}

type OpenAiOutputItem =
  | OpenAiFunctionCall
  | { type: "message"; content?: Array<{ type?: string; text?: string }> }
  | { type: string; [key: string]: unknown };

// ---------------------------------------------------------------------------
// History compaction
// ---------------------------------------------------------------------------

/**
 * `projectItems` returns a compacted COPY for the wire payload while the
 * canonical conversation stays intact for correctness. The most recent items
 * (the turn being continued) are preserved verbatim; for older items we
 * truncate big tool outputs to a head+tail preview and drop superseded
 * reasoning. Pairing is preserved - every `function_call` keeps its matching
 * `function_call_output` (only the output STRING shrinks), and the first item
 * (the user request + plan + workbook context) is always kept.
 *
 * A single-tool hop costs three items here (reasoning + `function_call` +
 * `function_call_output`), so twelve items is roughly four hops.
 */
const RECENT_INPUT_ITEMS_KEPT_FULL = 12;

/**
 * Builds the compact input array sent on the next Responses API request while
 * keeping the canonical in-memory conversation untouched.
 */
function projectInputForRequest(items: readonly unknown[]): unknown[] {
  const cutoff = Math.max(0, items.length - RECENT_INPUT_ITEMS_KEPT_FULL);
  const projected: unknown[] = [];
  for (let i = 0; i < items.length; i += 1) {
    const item = items[i];
    // Keep the seed user message and everything in the recent window verbatim.
    if (i === 0 || i >= cutoff) {
      projected.push(item);
      continue;
    }
    if (item && typeof item === "object") {
      const type = (item as { type?: unknown }).type;
      if (type === "reasoning") {
        // Superseded reasoning from an older, already-resolved turn.
        continue;
      }
      if (type === "function_call_output") {
        const out = (item as { output?: unknown }).output;
        if (typeof out === "string" && out.length > MAX_REPLAYED_TOOL_OUTPUT_CHARS) {
          projected.push({ ...(item as object), output: truncateReplayedToolOutput(out) });
          continue;
        }
      }
    }
    projected.push(item);
  }
  return projected;
}

// ---------------------------------------------------------------------------
// Response extraction
// ---------------------------------------------------------------------------

/**
 * Extracts concatenated `output_text` from a Responses API output array.
 */
function extractTextContent(output: OpenAiOutputItem[]): string {
  let text = "";
  for (const item of output) {
    if (item.type !== "message") continue;
    const msg = item as { type: "message"; content?: Array<{ type?: string; text?: string }> };
    if (!Array.isArray(msg.content)) continue;
    for (const part of msg.content) {
      if (part.type === "output_text" && typeof part.text === "string") {
        text += part.text;
      }
    }
  }
  return text;
}

/**
 * Extracts the optional reasoning summary returned by OpenAI Responses when
 * `reasoning.summary` is enabled. Shape is intentionally defensive because the
 * summary payload varies by model/API revision.
 */
function extractReasoningSummary(output: OpenAiOutputItem[]): string {
  const chunks: string[] = [];
  for (const item of output) {
    if (item.type !== "reasoning") continue;
    const summary = (item as { summary?: unknown }).summary;
    if (typeof summary === "string") {
      chunks.push(summary);
    } else if (Array.isArray(summary)) {
      for (const part of summary) {
        if (typeof part === "string") {
          chunks.push(part);
        } else if (part && typeof part === "object") {
          const text = (part as { text?: unknown }).text;
          if (typeof text === "string") {
            chunks.push(text);
          }
        }
      }
    }
  }
  return chunks.join("\n").trim();
}

// ---------------------------------------------------------------------------
// Adapter
// ---------------------------------------------------------------------------

/**
 * Flattens the canonical IR into Responses `input` items. Model turns are
 * spread back in verbatim so reasoning items keep their `function_call`
 * pairing; user and tool-result turns map one-to-one.
 */
function encodeTurns(turns: readonly AgentTurn[]): unknown[] {
  const items: unknown[] = [];
  for (const turn of turns) {
    if (turn.kind === "user") {
      items.push({ role: "user", content: turn.content });
      continue;
    }
    if (turn.kind === "model") {
      for (const item of turn.raw) {
        items.push(item);
      }
      continue;
    }
    items.push({
      type: "function_call_output",
      call_id: turn.callId,
      output: turn.output,
    });
  }
  return items;
}

function buildRequest({
  model,
  instructions,
  items,
  tools,
  forcedToolName,
  terminalTextFormat,
}: ExecutorBuildRequestParams): ExecutorProviderRequest {
  return {
    url: buildApiUrl("/api/ai/openai/responses"),
    body: {
      model: model.model,
      instructions,
      input: items,
      tools,
      tool_choice: forcedToolName
        ? { type: "function", name: forcedToolName }
        : "auto",
      reasoning: { effort: model.reasoningEffort, summary: "auto" },
      ...(terminalTextFormat === "plain_text"
        ? {}
        : { text: { format: { type: "json_object" } } }),
      parallel_tool_calls: true,
      stream: true,
    },
  };
}

async function streamTurn(
  params: ExecutorStreamTurnParams,
): Promise<AgentTurnResult> {
  // Stream output text for tool-call and terminal JSON extraction. Raw
  // reasoning summaries go only to the caller's polished-narrator sink.
  let streamedTextContent = "";
  const streamResult = await requestOpenAIResponsesStreamWithRetry({
    request: params.request,
    contextLabel: params.contextLabel,
    signal: params.signal,
    maxBodyRetries: params.maxBodyRetries,
    onStreamContent: (partial) => {
      streamedTextContent = partial;
      params.onText?.(partial);
    },
    onReasoningContent: params.onReasoning,
    onRetry: params.onRetry,
  });

  const data = streamResult.data as { output?: OpenAiOutputItem[] };
  const output: OpenAiOutputItem[] = Array.isArray(data.output) ? data.output : [];
  const toolCalls: AgentToolCall[] = output
    .filter((item): item is OpenAiFunctionCall => item.type === "function_call")
    .map((call) => ({
      callId: call.call_id,
      name: call.name,
      arguments: call.arguments,
    }));

  return {
    raw: output,
    toolCalls,
    // Prefer streamed text; fall back to extracting from the completed payload.
    text: streamedTextContent || extractTextContent(output),
    reasoning: streamResult.reasoningContent || extractReasoningSummary(output),
  };
}

export const openAiExecutorAdapter: ExecutorProviderAdapter = {
  provider: "openai",
  serializeTools: ({ scope, extraTools, options }: ExecutorSerializeToolsParams) =>
    buildToolDefinitionsForProvider("openai", scope, extraTools, options ?? {}) ?? [],
  encodeTurns,
  projectItems: projectInputForRequest,
  buildRequest,
  streamTurn,
};
